import ReviewCarousel from "@components/quiz/ReviewCarousel"
import StickyFooter from "@components/quiz/StickyFooter"
import { setQuizAnswer } from "@helpers/quizAnswers"
import { CheckCircleIcon } from "@heroicons/react/24/outline"
import { StarIcon } from "@heroicons/react/24/solid"
import { useEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import StepHeading from "./StepHeading"

const TOTAL_DURATION_MS = 10000
const TICK_MS = 40
const CHECKLIST = ["goals", "topics", "schedule", "plan"]
const ROW_SHARE = 100 / CHECKLIST.length
const POPUP_QUESTIONS = [
  { at: ROW_SHARE * 1.49, id: "popup-finish-what-you-start" },
  { at: ROW_SHARE * 2.62, id: "popup-remember-more" },
]

interface Props {
  onComplete: () => void
  onPopupAnswer: (question: string, answer: string) => void
}

export default function LoaderStep({ onComplete, onPopupAnswer }: Props) {
  const [progress, setProgress] = useState(0)
  const [activePopup, setActivePopup] = useState<string>()
  const answeredPopups = useRef(new Set<string>())
  const progressRef = useRef(0)
  const { t } = useTranslation()
  const isComplete = progress >= 100

  useEffect(() => {
    if (activePopup) return

    const interval = setInterval(() => {
      const current = progressRef.current
      const next = Math.min(current + (100 * TICK_MS) / TOTAL_DURATION_MS, 100)
      const popup = POPUP_QUESTIONS.find(
        (question) =>
          current < question.at &&
          next >= question.at &&
          !answeredPopups.current.has(question.id),
      )

      progressRef.current = popup ? popup.at : next
      setProgress(progressRef.current)

      if (popup) setActivePopup(popup.id)
      if (progressRef.current >= 100) clearInterval(interval)
    }, TICK_MS)

    return () => clearInterval(interval)
  }, [activePopup])

  const answerPopup = (answer: string) => {
    if (!activePopup) return

    answeredPopups.current.add(activePopup)
    setQuizAnswer(activePopup, [answer])
    onPopupAnswer(
      t(`pages.onboarding.loader.${activePopup}`, { lng: "en" }),
      answer,
    )
    setActivePopup(undefined)
  }

  return (
    <div className="flex grow flex-col gap-6 pt-4">
      <StepHeading
        headingKey={
          isComplete
            ? "pages.onboarding.loader.completeHeading"
            : "pages.onboarding.loader.heading"
        }
        key={isComplete ? "complete" : "loading"}
      />

      <ul className="divide-y divide-vm-line">
        {CHECKLIST.map((item, index) => {
          const rowProgress = Math.min(
            Math.max((progress - index * ROW_SHARE) / ROW_SHARE, 0),
            1,
          )
          const isDone = rowProgress >= 1
          const isStarted = index === 0 || progress >= index * ROW_SHARE

          if (!isStarted) return null

          return (
            <li className="animate-fade-up py-3.5" key={item}>
              <div className="flex items-center justify-between">
                <span className="text-base font-semibold text-vm-ink">
                  {t(
                    isDone
                      ? `pages.onboarding.loader.checklist.${item}`
                      : `pages.onboarding.loader.checklistActive.${item}`,
                  )}
                </span>

                {isDone ? (
                  <CheckCircleIcon className="h-6 w-6 animate-check-pop text-vm-primary" />
                ) : (
                  <span className="text-sm font-semibold tabular-nums text-vm-ink">
                    {Math.floor(rowProgress * 100)}%
                  </span>
                )}
              </div>

              {!isDone && (
                <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-vm-line">
                  <div
                    className="h-full rounded-full bg-vm-primary"
                    style={{ width: `${rowProgress * 100}%` }}
                  />
                </div>
              )}
            </li>
          )
        })}
      </ul>

      {isComplete ? (
        <StickyFooter>
          <button
            className="vm-button-primary animate-fade-up"
            onClick={onComplete}
            type="button"
          >
            {t("shared.continue")}
          </button>
        </StickyFooter>
      ) : (
        <div className="space-y-4 pb-6 pt-4">
          <div className="flex flex-col items-center gap-1">
            <div className="flex">
              {[0, 1, 2, 3, 4].map((star) => (
                <StarIcon className="h-6 w-6 text-vm-yellow" key={star} />
              ))}
            </div>
            <p className="text-center text-xl font-bold text-vm-ink">
              {t("pages.onboarding.loader.socialProof")}
            </p>
          </div>
          <ReviewCarousel />
        </div>
      )}

      {activePopup &&
        createPortal(
          <div className="fixed inset-0 z-50 flex animate-fade-in items-center justify-center bg-vm-backdrop px-5">
            <div className="w-full max-w-sm animate-scale-in rounded-card bg-vm-surface p-5 text-center shadow-card-hover">
              <p className="text-sm text-vm-muted">
                {t("pages.onboarding.loader.popupLabel")}
              </p>
              <p className="mt-1 text-balance text-lg font-bold text-vm-ink">
                {t(`pages.onboarding.loader.${activePopup}`)}
              </p>
              <div className="mt-5 grid grid-cols-2 gap-3">
                {(["no", "yes"] as const).map((answer) => (
                  <button
                    className="rounded-vm-md bg-vm-cream py-3.5 font-semibold text-vm-ink transition-[transform,background-color] hover:bg-vm-line active:scale-95"
                    key={answer}
                    onClick={() => answerPopup(answer)}
                    type="button"
                  >
                    {t(`pages.onboarding.${answer}`)}
                  </button>
                ))}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  )
}
